/*
 * mpetroff/print-maps
 * https://github.com/mpetroff/print-maps
 *
 * I used the source code from the above repository. Thanks so much!
 *
 * -----LICENSE------
 * Print Maps - High-resolution maps in the browser, for printing
 * Copyright (c) 2015-2020 Matthew Petroff
 *
 * Permission is hereby granted, free of charge, to any person obtaining a copy
 * of this software and associated documentation files (the "Software"), to deal
 * in the Software without restriction, including without limitation the rights
 * to use, copy, modify, merge, publish, distribute, sublicense, and/or sell
 * copies of the Software, and to permit persons to whom the Software is
 * furnished to do so, subject to the following conditions:
 *
 * The above copyright notice and this permission notice shall be included in
 * all copies or substantial portions of the Software.
 *
 * THE SOFTWARE IS PROVIDED "AS IS", WITHOUT WARRANTY OF ANY KIND, EXPRESS OR
 * IMPLIED, INCLUDING BUT NOT LIMITED TO THE WARRANTIES OF MERCHANTABILITY,
 * FITNESS FOR A PARTICULAR PURPOSE AND NONINFRINGEMENT. IN NO EVENT SHALL THE
 * AUTHORS OR COPYRIGHT HOLDERS BE LIABLE FOR ANY CLAIM, DAMAGES OR OTHER
 * LIABILITY, WHETHER IN AN ACTION OF CONTRACT, TORT OR OTHERWISE, ARISING FROM,
 * OUT OF OR IN CONNECTION WITH THE SOFTWARE OR THE USE OR OTHER DEALINGS IN
 * THE SOFTWARE.
 */

import { Feature, FeatureCollection, MultiPolygon, Point, Polygon, Position } from "geojson";
import { LngLatBounds, Map as MaplibreMap } from "maplibre-gl";
import { addressIconUrl } from "../../components/congregation/territory/maps";
import HourglassGlobals from "../../helpers/globals";
import { TerritoryAddress } from "../../types/scheduling/territory";

import { maptilerGlyphs } from "../../components/congregation/territory/common";
import { PaperDimensions, PaperSize, PDFOrientation } from "../../types/paper";

export enum Unit {
  in = "in",
  mm = "mm",
}

export enum MapKind {
  single,
  congregation,
}

const OneInchInMM = 25.4;

const Size = {
  // A0, A1, B0, B1 are not working well.
  // A0: [1189, 841],
  // A1: [841, 594],
  Letter: [279.4, 215.9],
  A2: [594, 420],
  A3: [420, 297],
  A4: [297, 210],
  A5: [210, 148],
  A6: [148, 105],
  // B0: [1414, 1000],
  // B1: [1000, 707],
  B2: [707, 500],
  B3: [500, 353],
  B4: [353, 250],
  B5: [250, 176],
  B6: [176, 125],
} as const;

function calculateSize(paperSize: PaperSize | PaperDimensions): PaperDimensions {
  if (typeof paperSize === "object" && paperSize.width && paperSize.height) {
    return paperSize;
  }
  return paperSize === "a4"
    ? { width: Size.A4[1], height: Size.A4[0] }
    : { width: Size.Letter[1], height: Size.Letter[0] };
}

// type Size = (typeof Size)[keyof typeof Size];

// export const PageOrientation = {
//   Landscape: "landscape",
//   Portrait: "portrait",
// } as const;
// type PageOrientation = (typeof PageOrientation)[keyof typeof PageOrientation];
//
// export const DPI = {
//   72: 72,
//   96: 96,
//   200: 200,
//   300: 300,
//   400: 400,
// } as const;
// type DPI = (typeof DPI)[keyof typeof DPI];

export class MapGenerator {
  private map: MaplibreMap;
  private readonly width: number;
  private readonly height: number;
  private dpi: number;
  private readonly unit: Unit;
  private readonly boundaries?: MultiPolygon;
  private readonly annotations?: FeatureCollection;
  private readonly addresses: TerritoryAddress[];
  private readonly markerFontStack: string[];
  private readonly orientation: PDFOrientation;
  private readonly invertedShading: boolean;
  private readonly kind: MapKind;
  private readonly textFontStack: string[];
  private readonly fullPage: boolean;

  constructor(
    map: MaplibreMap,
    boundaries?: MultiPolygon,
    annotations?: FeatureCollection,
    addresses: TerritoryAddress[] = [],
    fontStack: string[] = [],
    orientation: PDFOrientation = "portrait",
    invertedShading: boolean = false,
    fullPage: boolean = false,
    kind: MapKind = MapKind.single, // printing for one territory, or the entire congregation's territory?
    paperSize: PaperSize | PaperDimensions = HourglassGlobals.cong!.country.paper_size,
    dpi = 300,
    unit: Unit = Unit.mm,
    textFontStack: string[] = ["Open Sans Semibold", "Arial Unicode MS Bold"],
  ) {
    this.map = map;
    const size = calculateSize(paperSize);
    this.orientation = orientation;
    this.invertedShading = invertedShading;
    this.kind = kind;
    this.fullPage = fullPage;
    // we try to get the source image as close as we can to the final size, so at least the aspect ratio is correct
    // this way it can take up as much of the page as possible
    const oneInch = unit === Unit.mm ? OneInchInMM : 1;
    if (kind === MapKind.single) {
      const portraitHeight = fullPage ? size.height - oneInch - oneInch * 0.5 : size.height / 2;
      const landscapeHeightMargin = oneInch * 0.8;
      const landscapeHeight = fullPage ? size.width - landscapeHeightMargin : size.width * 0.75 - landscapeHeightMargin;
      this.width = this.orientation === "portrait" ? size.width - oneInch : size.height - oneInch;
      this.height = this.orientation === "portrait" ? portraitHeight : landscapeHeight;
    } else {
      this.width = size.width;
      this.height = size.height;
    }

    this.dpi = dpi;
    this.unit = unit;

    this.boundaries = boundaries;
    this.annotations = annotations;
    this.addresses = addresses.filter((a) => !!a.location);
    this.markerFontStack = fontStack;
    this.textFontStack = textFontStack;
  }

  /**
   * Generate and download Map image
   */
  async generate(): Promise<Blob> {
    const this_ = this;

    // Calculate pixel ratio
    const actualPixelRatio = window.devicePixelRatio;
    Object.defineProperty(window, "devicePixelRatio", {
      get() {
        return this_.dpi / 96;
      },
    });
    // Create map container
    const hidden = document.createElement("div");
    document.body.appendChild(hidden);
    const container = document.createElement("div");
    container.style.width = this.toPixels(this.width);
    container.style.height = this.toPixels(this.height);
    hidden.appendChild(container);

    const style = this.map.getStyle();
    if (style && style.sources) {
      const sources = style.sources;
      Object.keys(sources).forEach((name) => {
        const src = sources[name];
        if (src) {
          Object.keys(src).forEach((key) => {
            // delete properties if value is undefined.
            // for instance, raster-dem might has undefined value in "url" and "bounds"
            // @ts-ignore ignore this
            if (!src[key]) delete src[key];
          });
        }
      });
    }

    if (style && !style.glyphs) style.glyphs = maptilerGlyphs();

    // Render map
    const renderMap = new MaplibreMap({
      container,
      style,
      center: this.map.getCenter(),
      zoom: this.map.getZoom(),
      bearing: this.map.getBearing(),
      pitch: this.map.getPitch(),
      interactive: false,
      //preserveDrawingBuffer: true,
      fadeDuration: 0,
      attributionControl: false,
      validateStyle: false,
      // hack to read transform request callback function
      transformRequest: (this.map as any)._requestManager._transformRequestFn,
    });

    // comment this statement because an error is occured since maplibre v3. images[key].data has no value (null)
    // it looks working well in my style. let's see how it works without this code
    // the below code was added by https://github.com/watergis/maplibre-gl-export/pull/18.
    // const images = (this.map.style.imageManager || {}).images || [];
    // Object.keys(images).forEach((key) => {
    // 	renderMap.addImage(key, images[key].data);
    // });

    await renderMap.once("load");
    if (this.kind === MapKind.congregation) await this.populateCongregationMap(renderMap);
    else await this.populateMap(renderMap);
    await renderMap.once("idle");
    const canvas = renderMap.getCanvas();
    const blob: Blob = await new Promise<Blob>((resolve) =>
      canvas.toBlob((blob) => {
        if (blob) resolve(blob);
      }),
    );

    // cleanup
    renderMap.remove();
    hidden.parentNode?.removeChild(hidden);
    Object.defineProperty(window, "devicePixelRatio", {
      get() {
        return actualPixelRatio;
      },
    });
    hidden.remove();

    return blob;
  }

  /**
   * Convert mm/inch to pixel
   * @param length mm/inch length
   * @param conversionFactor DPI value. default is 96.
   */
  private toPixels(length: number, conversionFactor = 96) {
    if (this.unit === Unit.mm) {
      conversionFactor /= 25.4;
    }
    return `${conversionFactor * length}px`;
  }

  private async populateMap(renderMap: MaplibreMap) {
    const baseColor = "#3388FF";
    const bounds = new LngLatBounds();

    if (this.boundaries) {
      for (const coord of this.boundaries.coordinates) {
        for (const coord2 of coord) {
          // @ts-ignore it's ok
          coord2.forEach((c) => bounds.extend(c));
        }
      }
      if (this.invertedShading) {
        // create a new data geojson with the entire world and holes for the territory polygons
        const entireWorld: Position[] = [
          [-180, -90],
          [-180, 90],
          [180, 90],
          [180, -90],
          [-180, -90],
        ];
        const worldPolygon: Polygon = {
          type: "Polygon",
          coordinates: [entireWorld, ...this.boundaries.coordinates.flatMap((p) => (p[0] ? [p[0]] : []))],
        };
        renderMap.addSource("boundaries", {
          type: "geojson",
          data: worldPolygon,
        });
        renderMap.addLayer({
          id: "boundaries",
          type: "fill",
          source: "boundaries",
          layout: {},
          paint: {
            "fill-color": "#FFFFFF",
            "fill-opacity": 0.65,
          },
        });

        renderMap.addLayer({
          id: "boundariesOutline",
          type: "line",
          source: "boundaries",
          layout: {},
          paint: {
            "line-color": baseColor,
            "line-width": 2,
          },
        });
      } else {
        renderMap.addSource("boundaries", {
          type: "geojson",
          data: this.boundaries,
        });

        renderMap.addLayer({
          id: "boundaries",
          type: "fill",
          source: "boundaries",
          layout: {},
          paint: {
            "fill-color": baseColor,
            "fill-opacity": 0.4,
          },
        });

        renderMap.addLayer({
          id: "boundariesOutline",
          type: "line",
          source: "boundaries",
          layout: {},
          paint: {
            "line-color": baseColor,
            "line-width": 3,
          },
        });
      }
    }

    if (this.annotations) {
      renderMap.addSource("annotations", {
        type: "geojson",
        data: this.annotations,
      });

      this.annotations.features.forEach((f) => {
        if (f.geometry.type === "LineString") {
          // @ts-ignore it's ok
          f.geometry.coordinates.forEach((c) => bounds.extend(c));
        } else if (f.geometry.type === "Point") {
          // @ts-ignore it's ok
          bounds.extend(f.geometry.coordinates);
        }
      });

      renderMap.addLayer({
        id: "lines",
        type: "line",
        source: "annotations",
        layout: {},
        paint: {
          "line-color": baseColor,
          "line-width": 3,
        },
      });

      renderMap.addLayer({
        id: "labels",
        type: "symbol",
        source: "annotations",
        layout: {
          "text-field": ["get", "text"],
          "text-variable-anchor": ["top", "bottom", "left", "right"],
          "text-justify": "auto",
          "text-padding": 2,
          "text-font": this.textFontStack,
        },
        paint: {
          "text-color": "#000000",
          "text-halo-color": "#F3C600",
          "text-halo-width": 2,
          "text-halo-blur": 1,
        },
      });
    }

    // we can't use regular markers, because they aren't part of the canvas
    // we need to add a symbol layer for the markers
    const addressMarkerFontStack =
      this.markerFontStack.length > 0 ? this.markerFontStack : ["Open Sans Regular", "Arial Unicode MS Regular"];
    if (Array.isArray(this.addresses) && this.addresses.length > 0) {
      const dncAddr = this.addresses.find((a) => a.dnc);

      if (dncAddr) {
        // we need the DNC marker image
        const img = await this.getImage(addressIconUrl(dncAddr, true));
        renderMap.addImage("dncMarker", img);

        const dncFeatures = this.addresses
          .filter((a) => a.dnc)
          .map((a): Feature<Point> => {
            return {
              type: "Feature",
              properties: { text: a.pinLabel },
              geometry: { type: "Point", coordinates: [a.location?.x ?? 0, a.location?.y ?? 0] },
            };
          });

        renderMap.addSource("dncPoints", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: dncFeatures,
          },
        });
        renderMap.addLayer({
          id: "dncLayer",
          type: "symbol",
          source: "dncPoints",
          paint: {
            "text-color": "#FFFFFF",
          },
          layout: {
            "symbol-placement": "point",
            "icon-image": "dncMarker",
            "icon-text-fit": "both",
            "icon-text-fit-padding": [2, 4, 2, 4],
            "text-field": ["get", "text"],
            "text-font": addressMarkerFontStack,
            "text-variable-anchor": ["bottom", "center", "top", "left", "right"],
            "text-variable-anchor-offset": ["bottom", [0, 0.5], "top", [0, -1]],
            "text-justify": "auto",
            "text-size": 10,
            "text-allow-overlap": true,
          },
        });
      }

      const regAddr = this.addresses.find((a) => !a.dnc);
      if (regAddr) {
        const img = await this.getImage(addressIconUrl(regAddr, true));
        renderMap.addImage("regMarker", img);

        const regFeatures = this.addresses
          .filter((a) => !a.dnc)
          .map((a): Feature<Point> => {
            return {
              type: "Feature",
              properties: { text: a.pinLabel },
              geometry: { type: "Point", coordinates: [a.location?.x ?? 0, a.location?.y ?? 0] },
            };
          });

        renderMap.addSource("regPoints", {
          type: "geojson",
          data: {
            type: "FeatureCollection",
            features: regFeatures,
          },
        });
        renderMap.addLayer({
          id: "regLayer",
          type: "symbol",
          source: "regPoints",
          paint: {
            "text-color": "#FFFFFF",
          },
          layout: {
            "symbol-placement": "point",
            "icon-image": "regMarker",
            "icon-text-fit": "both",
            "icon-text-fit-padding": [2, 4, 2, 4],
            "text-field": ["get", "text"],
            "text-font": addressMarkerFontStack,
            "text-variable-anchor": ["bottom", "center", "top", "left", "right"],
            "text-variable-anchor-offset": ["bottom", [0, 0.5], "top", [0, -1]],
            "text-justify": "auto",
            "text-size": 10,
            "text-allow-overlap": true,
          },
        });
      }
    }

    if (!bounds.isEmpty()) {
      if (bounds.getNorthEast().distanceTo(bounds.getSouthWest()) < 100) {
        renderMap.setCenter(bounds.getCenter());
      } else {
        renderMap.fitBounds(bounds, { padding: 50, animate: false });
      }
    }
  }

  private async populateCongregationMap(renderMap: MaplibreMap) {
    const baseColor = "#3388FF";
    const bounds = new LngLatBounds();

    // this.boundaries represents the overall congregation territory boundary
    if (this.boundaries) {
      for (const coord of this.boundaries.coordinates) {
        for (const coord2 of coord) {
          // @ts-ignore it's ok
          coord2.forEach((c) => bounds.extend(c));
        }
      }

      renderMap.addSource("boundaries", {
        type: "geojson",
        data: this.boundaries,
      });

      renderMap.addLayer({
        id: "boundariesOutline",
        type: "line",
        source: "boundaries",
        layout: {},
        paint: {
          "line-color": baseColor,
          "line-width": 3,
        },
      });
    }

    // this.annotations is the collection of all individual territories
    if (this.annotations) {
      const territoryColor = "#008800";

      renderMap.addSource("territories", {
        type: "geojson",
        data: this.annotations,
      });
      renderMap.addLayer({
        id: `territoriesLayer`,
        type: "fill",
        source: "territories",
        layout: {},
        paint: {
          "fill-color": territoryColor,
          "fill-opacity": 0.2, // match the leaflet default so it appears close to on screen
        },
        filter: ["==", "$type", "Polygon"],
      });
      renderMap.addLayer({
        id: `territoriesOutlineLayer`,
        type: "line",
        source: "territories",
        layout: {},
        paint: {
          "line-color": territoryColor,
          "line-width": 2,
        },
        filter: ["==", "$type", "Polygon"],
      });

      // the labels are sent as separate Points because we could not get symbol-placement line-center to work
      renderMap.addLayer({
        id: "territoryLabels",
        type: "symbol",
        source: "territories",
        layout: {
          "symbol-placement": "point",
          "text-field": ["get", "label"],
          "text-anchor": "center",
          "text-justify": "center",
          "text-padding": 2,
          "text-size": 15,
          "text-allow-overlap": true,
          "text-font": this.textFontStack,
        },
        paint: {
          "text-color": "#FFFFFF",
        },
        filter: ["==", "$type", "Point"],
      });
    }

    if (!bounds.isEmpty()) {
      if (bounds.getNorthEast().distanceTo(bounds.getSouthWest()) < 100) {
        renderMap.setCenter(bounds.getCenter());
      } else {
        renderMap.fitBounds(bounds, { padding: 50 });
      }
    }
  }

  private async getImage(dataUri: string): Promise<HTMLImageElement> {
    return new Promise<HTMLImageElement>((resolve, reject) => {
      const img = new Image();
      img.onload = () => resolve(img);
      img.onerror = reject;
      img.src = dataUri;
    });
  }
}
